/**
 * Silent capture (Didit "Silent Capture"): while the applicant photographs their
 * ID, take one frame from the FRONT camera. Only when the step asks for it
 * (`snapshot.step.silent_capture`, from the pinned workflow).
 *
 * Phones cannot run both cameras at once in a browser, so the frame is taken
 * after the document shot is confirmed, once the rear camera has stopped. Every
 * failure — no front camera, permission, a busy device — is swallowed: the frame
 * is supporting evidence, never a reason to block the document step.
 */
import { blobToBase64 } from './capture';

/** How long to let the front camera expose before taking the frame. */
const SETTLE_MS = 600;

export function silentCaptureOf(step: unknown): boolean {
  return (
    typeof step === 'object' &&
    step !== null &&
    (step as { silent_capture?: unknown }).silent_capture === true
  );
}

/** One JPEG frame from the front camera, as base64 — or null. */
export async function grabFrontFrame(): Promise<string | null> {
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    await new Promise((r) => setTimeout(r, SETTLE_MS));
    if (!video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
    return blob ? await blobToBase64(blob) : null;
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}
